import type { Currency } from '../currencies';

/**
 * Exchange-rate lookups over the Numtable library's daily history. Pure, so the
 * browser (`./history`, which loads it) and the Worker (which refreshes sheets
 * server-side) read rates the same way.
 */

export const FX_MARKETS = ['oficial', 'mep', 'blue'] as const;
export type FxMarket = (typeof FX_MARKETS)[number];
export const FX_SIDES = ['buy', 'sell', 'avg'] as const;
export type FxSide = (typeof FX_SIDES)[number];

export interface Quote { buy: number | null; sell: number | null; avg: number | null }
export interface Sheet { currency: string; columns: string[]; rows: { date: string; values: Record<string, Quote> }[] }

/** date (YYYY-MM-DD) → column → quote, for the USD and ARS sheets. */
export interface FxHistory {
  readonly usd: ReadonlyMap<string, Readonly<Record<string, Quote>>>;
  readonly ars: ReadonlyMap<string, Readonly<Record<string, Quote>>>;
  /** Every date with data, newest first. */
  readonly dates: readonly string[];
}

const MARKET_LABEL: Record<FxMarket, string> = { oficial: 'Oficial', mep: 'MEP', blue: 'Blue' };

/** Units of `currency` per 1 USD on `date`, or null when that day has no quote. */
function perUsd(history: FxHistory, currency: Currency, date: string, market: FxMarket, side: FxSide): number | null {
  if (currency === 'USD') return 1;
  const quote = currency === 'ARS'
    ? history.ars.get(date)?.[`USD ${MARKET_LABEL[market]}`]
    : history.usd.get(date)?.[currency];
  // Official rates of other currencies are a mid rate: every side is the same.
  const value = currency === 'ARS' ? quote?.[side] : quote?.avg;
  return typeof value === 'number' && value > 0 ? value : null;
}

/**
 * Units of `to` per 1 `from` on `date`. A day without a quote for the pair
 * (a weekend, a holiday, today before the close) takes the latest earlier day
 * that has one. Null when there is none at all.
 */
export function fxRateOn(
  history: FxHistory,
  pair: { from: Currency; to: Currency; market?: FxMarket | undefined; side?: FxSide | undefined },
  date: string,
): number | null {
  if (pair.from === pair.to) return 1;
  const market = pair.market ?? 'oficial';
  const side = pair.side ?? 'avg';
  for (const day of history.dates) {
    if (day > date) continue;
    const from = perUsd(history, pair.from, day, market, side);
    const to = perUsd(history, pair.to, day, market, side);
    if (from !== null && to !== null) return to / from;
  }
  return null;
}

/** The history from the library document's USD and ARS sheets. */
export function fxHistoryFromSheets(sheets: readonly Sheet[]): FxHistory {
  const byDate = (currency: string) =>
    new Map((sheets.find((s) => s.currency === currency)?.rows ?? []).map((row) => [row.date, row.values]));
  const usd = byDate('USD');
  const ars = byDate('ARS');
  const dates = [...new Set([...usd.keys(), ...ars.keys()])].sort().reverse();
  return { usd, ars, dates };
}
